/**
 * Nomenclature picker modal для документів реалізації (2026-06-02).
 *
 * Власник 2026-06-02: «в першій колонці замість випадашки зробити кнопку
 * додати — при кліку на неї з'являється попап там поля фільтр по складам,
 * фільтр за буквами, в таблиці товару колонки назва — одиниця виміру —
 * доступна кількість».
 *
 * Використовується у GoodsSaleEditor (Реалізація): кнопка «+ Додати товар»
 * у першій колонці → відкриває цей попап → бухгалтер бачить актуальні
 * залишки на дату документа + може фільтрувати по складах і назві.
 *
 * ч.1360: довідник тягнеться ЦІЛКОМ (`nomApi.listAll()`, один ключ `nomenclature-full`):
 * з `limit: 1000` у акаунті на 1394 позиції «Юридичні послуги» (за абеткою після першої
 * тисячі) не було у підборі послуг навіть пошуком.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { refs } from '@/api/refs';
import { reports, type GoodsBalanceRow } from '@/api/documents';
import { nomenclature as nomApi } from '@/api/catalogs';


interface Props {
  /** Організація — обов'язково (для запиту залишків). */
  organizationId: string;
  /** Дата документа (для on_date залишків). */
  documentDate: string;
  /** Початковий warehouse (опц.) — використовується як default фільтр. */
  defaultWarehouseId?: string | null;
  /** Колбек при виборі товару. */
  onPick: (row: GoodsBalanceRow) => void;
  /** Закриття попапу. */
  onClose: () => void;
  /**
   * 2026-08-11 (рішення власника, переміщення): МУЛЬТИВИБІР галочками —
   * клік по рядку ставить/знімає галочку, кнопка «додати обрані (N)»
   * віддає всі разом через onPickMany.
   */
  multiSelect?: boolean;
  onPickMany?: (rows: GoodsBalanceRow[]) => void;
  /**
   * Режим catalog (2026-06-02): показуємо ВСI номенклатури, без перевірки
   * залишків. Корисно для Receipts (купівля від постачальника — товар
   * тільки прибуває, залишків може й не бути). За замовч false — sale mode.
   */
  catalogMode?: boolean;
  /**
   * 2026-07-24 (рішення власника): дефолт галочки «лише з рухом у цій
   * організації» — приховує номенклатуру інших орг tenant'а (без залишку
   * тут). Послуги показуються завжди (не складські). Для замовлення/рахунку
   * вмикаємо, для надходження (нові товари) — ні.
   */
  defaultOnlyWithStock?: boolean;
  /**
   * 2026-07-25: обмежити підбір лише товарами або лише послугами
   * (вкладки Товари/Послуги замовлення). undefined — усі.
   */
  onlyKind?: 'goods' | 'services';
  /**
   * 2026-07-27 (мультисклад 0386/0388): у catalog-режимі РОЗГОРТАТИ товарні
   * рядки ПО СКЛАДАХ з залишком (колонка «Склад», вибір несе warehouse_id
   * рядка). Товар без залишку — один рядок без складу; послуги — без складу.
   */
  expandByWarehouse?: boolean;
  /**
   * ч.666: операції ОДНОГО складу (комплектація/розукомплектація) —
   * фільтр складу зафіксовано складом шапки документа («що бачиш —
   * те й спишеться»), перемикач задизейблено.
   */
  lockWarehouse?: boolean;
}

export default function NomenclaturePickerModal({
  organizationId,
  documentDate,
  defaultWarehouseId,
  onPick,
  onClose,
  catalogMode = false,
  defaultOnlyWithStock = false,
  onlyKind,
  expandByWarehouse = false,
  lockWarehouse = false,
  multiSelect = false,
  onPickMany,
}: Props) {
  const [warehouseId, setWarehouseId] = useState<string>(defaultWarehouseId ?? '');
  // Мультивибір: ключ рядка → рядок (unique warehouse+nomenclature+idx).
  const [selected, setSelected] = useState<Map<string, GoodsBalanceRow>>(new Map());
  const [search, setSearch] = useState('');
  // Порція А (2026-07-24): фільтр за видом номенклатури.
  const [kindId, setKindId] = useState('');
  const [onlyWithStock, setOnlyWithStock] = useState(defaultOnlyWithStock);

  const warehousesQ = useQuery({
    queryKey: ['warehouses-for-picker', organizationId],
    queryFn: () => refs.warehouses(organizationId),
    enabled: !!organizationId,
  });
  const kindsQ = useQuery({
    queryKey: ['nomenclature-kinds'],
    queryFn: refs.nomenclatureKinds.list,
    staleTime: 300_000,
  });
  // 2026-06-02: Nomenclature з base_uom_id + UOM list для display.
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({
    queryKey: ['uoms-for-picker'],
    queryFn: refs.uoms,
  });
  // Карта nomenclature_id → uom_short_name
  const uomShortByNomId = useMemo(() => {
    const m = new Map<string, string>();
    const uomById = new Map<string, string>();
    for (const u of uomsQ.data ?? []) {
      uomById.set(u.id, u.short_name);
    }
    for (const n of nomListQ.data?.items ?? []) {
      if (n.base_uom_id) {
        const short = uomById.get(n.base_uom_id);
        if (short) m.set(n.id, short);
      }
    }
    return m;
  }, [nomListQ.data, uomsQ.data]);

  // Балансы на дату документа (только в sale mode — в catalog mode не нужны).
  const balancesQ = useQuery({
    queryKey: ['goods-balance', organizationId, documentDate, warehouseId],
    queryFn: () =>
      reports.goodsBalance({
        organization_id: organizationId,
        on_date: documentDate,
        warehouse_id: warehouseId || undefined,
      }),
    enabled: !!organizationId && !!documentDate,
  });

  // У catalog mode будуємо рядки з nomenclature; кількість підтягуємо із
  // залишків (Порція А: «виводити залишки» і в каталозі — з урахуванням
  // фільтра складу; послуги показуються без кількості).
  const qtyByNom = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of balancesQ.data?.rows ?? []) {
      if (warehouseId && r.warehouse_id !== warehouseId) continue;
      m.set(r.nomenclature_id, (m.get(r.nomenclature_id) ?? 0) + Number(r.quantity));
    }
    return m;
  }, [balancesQ.data, warehouseId]);
  const catalogRows = useMemo<GoodsBalanceRow[]>(() => {
    if (!catalogMode) return [];
    const noms = (nomListQ.data?.items ?? []).filter((n) => !n.deletion_mark);
    if (!expandByWarehouse) {
      return noms.map((n) => ({
        warehouse_id: '',
        warehouse_code: '',
        warehouse_name: '',
        nomenclature_id: n.id,
        nomenclature_code: n.code,
        nomenclature_article: n.article,
        nomenclature_name: n.name,
        quantity: String(qtyByNom.get(n.id) ?? 0),
        cost: null,
      }));
    }
    // 0386/0388: товар розгортається ПО СКЛАДАХ з ненульовим залишком —
    // вибір рядка несе конкретний склад (резерв/списання по-складово).
    const byNomWh = new Map<string, GoodsBalanceRow[]>();
    for (const r of balancesQ.data?.rows ?? []) {
      if (warehouseId && r.warehouse_id !== warehouseId) continue;
      if (Number(r.quantity) === 0) continue;
      const list = byNomWh.get(r.nomenclature_id) ?? [];
      list.push(r);
      byNomWh.set(r.nomenclature_id, list);
    }
    const out: GoodsBalanceRow[] = [];
    for (const n of noms) {
      const whRows = byNomWh.get(n.id);
      if (whRows?.length) {
        for (const r of whRows) {
          out.push({
            ...r,
            nomenclature_code: n.code,
            nomenclature_article: n.article,
            nomenclature_name: n.name,
          });
        }
      } else {
        out.push({
          warehouse_id: '', warehouse_code: '', warehouse_name: '',
          nomenclature_id: n.id, nomenclature_code: n.code,
          nomenclature_article: n.article, nomenclature_name: n.name,
          quantity: '0', cost: null,
        });
      }
    }
    return out;
  }, [catalogMode, expandByWarehouse, nomListQ.data, qtyByNom, balancesQ.data, warehouseId]);

  // Карта nomenclature_id → kind_id (для фільтра за видом).
  const kindByNomId = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const n of nomListQ.data?.items ?? []) m.set(n.id, n.kind_id);
    return m;
  }, [nomListQ.data]);

  // Карта nomenclature_id → is_service (послуги не складські — не фільтруємо).
  const serviceByNomId = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const n of nomListQ.data?.items ?? []) m.set(n.id, !!n.is_service);
    return m;
  }, [nomListQ.data]);

  const filteredRows = useMemo(() => {
    let rows = catalogMode ? catalogRows : (balancesQ.data?.rows ?? []);
    if (onlyKind) {
      const wantService = onlyKind === 'services';
      rows = rows.filter((r) => !!serviceByNomId.get(r.nomenclature_id) === wantService);
    }
    if (kindId) {
      rows = rows.filter((r) => kindByNomId.get(r.nomenclature_id) === kindId);
    }
    if (onlyWithStock) {
      // ч.674: «лише з залишком» = додатний залишок. Від'ємний (пересорт/
      // мінус по обліку) — це НЕ наявність, списувати з нього не можна.
      // ч.675: діє і в sale-режимі (балансовому) — раніше фільтр працював
      // лише у catalogMode, і мінусові рядки балансу пролазили у підбір.
      rows = rows.filter((r) =>
        Number(r.quantity) > 0 || serviceByNomId.get(r.nomenclature_id));
    }
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.nomenclature_name.toLowerCase().includes(q) ||
      (r.nomenclature_code ?? '').toLowerCase().includes(q) ||
      (r.nomenclature_article ?? '').toLowerCase().includes(q),
    );
  }, [balancesQ.data, catalogRows, catalogMode, search, kindId, kindByNomId, onlyWithStock, serviceByNomId, onlyKind]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 900, maxHeight: '85vh',
          background: 'var(--bg-panel)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ margin: 0 }}>{onlyKind === 'services' ? 'Додати послугу' : 'Додати товар'}</h3>
          <button onClick={onClose} style={{ padding: '0.3rem 0.6rem' }}>×</button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--border)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 2fr',
          gap: '0.75rem',
        }}>
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>
              Склад
            </label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              disabled={lockWarehouse}
              title={lockWarehouse
                ? 'Склад зафіксовано складом документа — компоненти списуються саме з нього'
                : undefined}
              style={{ width: '100%' }}
            >
              <option value="">— Усі склади —</option>
              {warehousesQ.data?.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>
              Вид номенклатури
            </label>
            <select
              value={kindId}
              onChange={(e) => setKindId(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">— Усі види —</option>
              {(kindsQ.data ?? []).filter((k) => !k.deletion_mark).map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>
              Пошук (назва, код, артикул)
            </label>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Почніть набирати назву товару…"
              style={{ width: '100%' }}
            />
          </div>
          <label style={{
            gridColumn: '1 / -1', display: 'flex', alignItems: 'center',
            gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer',
          }}>
            <input type="checkbox" checked={onlyWithStock}
              onChange={(e) => setOnlyWithStock(e.target.checked)}
              style={{ width: 'auto' }} />
            лише з залишком у цій організації
            <span style={{ color: 'var(--muted)' }}>
              {catalogMode
                ? '(ховає товари інших організацій; послуги видно завжди)'
                : '(ховає рядки з нульовим/мінусовим залишком)'}
            </span>
          </label>
        </div>

        {/* Body — list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0' }}>
          {(catalogMode ? nomListQ.isLoading : balancesQ.isLoading) ? (
            <div style={{ padding: '1rem', color: 'var(--muted)' }}>
              {catalogMode ? 'Завантаження довідника…' : 'Завантаження залишків…'}
            </div>
          ) : filteredRows.length === 0 ? (
            <div style={{ padding: '1rem', color: 'var(--muted)' }}>
              {search
                ? 'Нічого не знайдено за вашим запитом.'
                : catalogMode
                ? 'Довідник номенклатури порожній.'
                : (warehouseId
                    ? 'На цьому складі немає товарів з залишками.'
                    : 'Залишки відсутні. Можливо ще не було Надходжень.')}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left', position: 'sticky', top: 0, fontSize: '0.72rem' }}>
                  {multiSelect && <th style={{ padding: '0.2rem 0.4rem', width: 30 }}></th>}
                  <th style={{ padding: '0.2rem 0.4rem', width: 100 }}>Код</th>
                  <th style={{ padding: '0.2rem 0.4rem' }}>Назва</th>
                  <th style={{ padding: '0.2rem 0.4rem', width: 80, textAlign: 'center' }}>Од.</th>
                  {((!catalogMode && !warehouseId) || (catalogMode && expandByWarehouse)) && (
                    <th style={{ padding: '0.2rem 0.4rem', width: 160 }}>Склад</th>
                  )}
                  <th style={{ padding: '0.2rem 0.4rem', width: 130, textAlign: 'right' }}>
                    {catalogMode ? 'Залишок' : 'Доступна к-ть'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, i) => {
                  const rowKey = `${r.warehouse_id}-${r.nomenclature_id}-${i}`;
                  const isSel = selected.has(rowKey);
                  return (
                  <tr
                    key={rowKey}
                    onClick={() => {
                      if (!multiSelect) { onPick(r); return; }
                      setSelected((m) => {
                        const next = new Map(m);
                        if (next.has(rowKey)) next.delete(rowKey);
                        else next.set(rowKey, r);
                        return next;
                      });
                    }}
                    style={{
                      cursor: 'pointer',
                      borderTop: '1px solid var(--border-soft)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = isSel ? '#eff6ff' : 'white')}
                    {...(isSel ? { 'data-selected': 1 } : {})}
                  >
                    {multiSelect && (
                      <td style={{ padding: '0.2rem 0.4rem', width: 30, textAlign: 'center' }}>
                        <input type="checkbox" checked={isSel} readOnly
                          style={{ width: 'auto', pointerEvents: 'none' }} />
                      </td>
                    )}
                    <td style={{ padding: '0.2rem 0.4rem', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {r.nomenclature_code ?? '—'}
                    </td>
                    <td style={{ padding: '0.2rem 0.4rem' }}>{r.nomenclature_name}</td>
                    <td style={{ padding: '0.2rem 0.4rem', textAlign: 'center', color: 'var(--muted)' }}>
                      {uomShortByNomId.get(r.nomenclature_id) ?? '—'}
                    </td>
                    {((!catalogMode && !warehouseId) || (catalogMode && expandByWarehouse)) && (
                      <td style={{ padding: '0.2rem 0.4rem', color: 'var(--muted)', fontSize: '0.72rem' }}>
                        {r.warehouse_name || '—'}
                      </td>
                    )}
                    <td style={{ padding: '0.2rem 0.4rem', textAlign: 'right', fontWeight: 600 }}>
                      {Number(r.quantity).toLocaleString('uk-UA', { maximumFractionDigits: 3 })}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.6rem 1rem',
          borderTop: '1px solid var(--border)',
          fontSize: '0.85rem',
          color: 'var(--muted)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {filteredRows.length > 0 && (() => {
              const total = catalogMode
                ? (catalogRows.length)
                : (balancesQ.data?.rows.length ?? 0);
              return (
                <span>
                  Знайдено: <strong>{filteredRows.length}</strong>
                  {filteredRows.length !== total && <span> з {total}</span>}
                  {' • '}{multiSelect
                    ? 'Клік — позначити/зняти; можна обрати кілька'
                    : 'Натисніть на рядок щоб обрати'}
                </span>
              );
            })()}
            {multiSelect && (
              <button className="primary" style={{ marginLeft: 'auto' }}
                disabled={selected.size === 0}
                onClick={() => {
                  // ч.664: «додати обрані» додає І закриває попап (зауваження
                  // власника — попап лишався відкритим).
                  onPickMany?.([...selected.values()]);
                  onClose();
                }}>
                додати обрані ({selected.size})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
